import { useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Coins } from 'lucide-react'
import { getCupsForPhase } from '@brochure/shared'
import { useMatch } from '@/hooks/useMatch'
import { useMatchTimer } from '@/hooks/useMatchTimer'
import { takenCups, redemptionTeamId } from '@/lib/cup-state'
import { cupLayout } from '@/lib/match-clock'
import { nextFlashTarget } from '@/lib/rearrangement-flash'
import { LocalizedLink } from '@/components/LocalizedLink'
import { Scoreboard } from '@/components/stoccopong/Scoreboard'
import { MatchBoard } from '@/components/stoccopong/MatchBoard'
import { MatchHeader, matchBackClass } from '@/components/stoccopong/MatchHeader'
import { EventLog } from '@/components/stoccopong/EventLog'
import { LoadError } from '@/components/stoccopong/LoadError'
import { SegmentedTabs } from '@/components/ui/SegmentedTabs'
import { cn } from '@/lib/utils'

const TABS = ['match', 'log'] as const
type TabKey = (typeof TABS)[number]

// Quanto resta acceso il lampeggio argento sui bicchieri appena spostati (in ms).
const SILVER_FLASH_MS = 2000

/** Pagina pubblica, di sola lettura, di una partita: variante "notte" sotto una fascia bg-paper alta quanto
 * l'header fisso del sito. Stesso tavolo e stesso tabellone dell'arbitro, senza comandi. */
export function MatchPage() {
  const { t } = useTranslation()
  const { id, matchId } = useParams<{ id: string; matchId: string }>()
  const { data, error } = useMatch(id, matchId)
  const [searchParams, setSearchParams] = useSearchParams()
  const elapsed = useMatchTimer(data?.match)

  // Un nuovo spostamento fa lampeggiare argento, per 2s, i bicchieri della squadra bersaglio. La
  // dipendenza è SOLO la lunghezza dell'array, non l'intero `data`: `useMatch` restituisce un oggetto
  // nuovo a ogni messaggio WebSocket (anche per eventi non collegati, es. un bicchiere segnato altrove),
  // e se l'effetto scattasse a ogni refetch la cleanup (`clearTimeout`) interromperebbe il lampeggio
  // in corso senza mai reimpostarlo, lasciandolo acceso per sempre.
  const previousRearrangements = useRef<number | null>(null)
  const [flashTeamId, setFlashTeamId] = useState<string | null>(null)
  useEffect(() => {
    // Cambio partita (o primo caricamento): niente confronto con la partita precedente.
    if (!data) {
      previousRearrangements.current = null
      return
    }
    const target = nextFlashTarget(previousRearrangements.current, data.rearrangements)
    previousRearrangements.current = data.rearrangements.length
    if (!target) return
    setFlashTeamId(target)
    const timer = setTimeout(() => setFlashTeamId(null), SILVER_FLASH_MS)
    return () => clearTimeout(timer)
  }, [data?.rearrangements.length])

  if (!data && error) {
    return (
      <main id="main" className="mx-auto max-w-7xl px-4 pt-28 pb-section sm:px-6 lg:px-8">
        <LoadError error={error} className="text-ink">
          <LocalizedLink to="stoccoPongTournament" params={{ id: id! }} className="mt-4 inline-block font-body text-sm font-semibold text-sea-ink underline-offset-4 hover:underline">
            {t('stoccoPong.match.back')}
          </LocalizedLink>
        </LoadError>
      </main>
    )
  }
  if (!data) return null

  const { match, homeTeam, awayTeam, maxCups } = data
  const taken = takenCups(data)
  // L'oro segna la fase del pareggio in corso: `redemptionState` non conosce lo stato della partita,
  // quindi da sola resterebbe vera anche a partita conclusa (il vincitore avrebbe per sempre bicchieri
  // dorati). Vale solo mentre la partita è `live`.
  const losing = match.status === 'live' ? redemptionTeamId(data) : null
  const cupCount = getCupsForPhase(match.phase, data.settings) as 3 | 6
  const layout = cupLayout(cupCount)
  const requested = searchParams.get('tab')
  const active: TabKey = (TABS as readonly string[]).includes(requested ?? '') ? (requested as TabKey) : 'match'
  const logCount = data.cupEvents.length + data.rearrangements.length + data.airballs.length

  function selectTab(tab: TabKey) {
    const next = new URLSearchParams(searchParams)
    next.set('tab', tab)
    setSearchParams(next, { replace: true })
  }

  return (
    <>
      <div aria-hidden="true" className="h-16 bg-paper md:h-20" />
      <main id="main" className="night min-h-dvh">
        <MatchHeader
          match={match}
          className="top-16 md:top-20"
          status={
            <LocalizedLink
              to="stoccoPongStockbetMatch"
              params={{ id: id!, matchId: match.id }}
              data-testid="match-stockbet-link"
              className="flex min-h-11 shrink-0 items-center"
            >
              <span className="flex items-center gap-1.5 rounded-full bg-sun px-2.5 py-1 font-body text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-ink transition-transform duration-150 hover:-translate-y-px active:translate-y-0">
                <Coins className="size-3.5" aria-hidden="true" />
                {t('stoccoPong.match.stockbet')}
              </span>
            </LocalizedLink>
          }
          back={
            <LocalizedLink to="stoccoPongTournament" params={{ id: id! }} aria-label={t('stoccoPong.match.back')} title={t('stoccoPong.match.back')} className={matchBackClass}>
              <ArrowLeft className="size-5" aria-hidden="true" />
            </LocalizedLink>
          }
        >
          <Scoreboard detail={data} elapsed={elapsed} />
        </MatchHeader>

        <div className="mx-auto max-w-lg px-4 pt-4 pb-section md:grid md:max-w-5xl md:grid-cols-[minmax(0,1fr)_22rem] md:items-start md:gap-8 md:pt-6">
          <SegmentedTabs
            tabs={[
              { key: 'match', label: t('stoccoPong.match.tabs.match') },
              { key: 'log', label: t('stoccoPong.match.tabs.log', { count: logCount }) },
            ]}
            active={active}
            onSelect={selectTab}
            ariaLabel={t('stoccoPong.match.tabs.ariaLabel')}
            idPrefix="match-tab"
            panelId={`match-panel-${active}`}
            tone="night"
            fill
            className="mb-4 md:hidden"
          />

          <section role="tabpanel" id="match-panel-match" aria-labelledby="match-tab-match" className={cn(active !== 'match' && 'max-md:hidden')}>
            <MatchBoard
              layout={layout}
              maxCups={maxCups}
              home={{ team: homeTeam, taken: taken.home, gold: losing === awayTeam.id, silverFlash: flashTeamId === homeTeam.id }}
              away={{ team: awayTeam, taken: taken.away, gold: losing === homeTeam.id, silverFlash: flashTeamId === awayTeam.id }}
            />
          </section>

          <section role="tabpanel" id="match-panel-log" aria-labelledby="match-tab-log" className={cn(active !== 'log' && 'max-md:hidden')}>
            <h2 className="mb-3 hidden font-body text-sm font-semibold text-paper/70 md:block">{t('stoccoPong.match.tabs.log', { count: logCount })}</h2>
            <EventLog detail={data} />
          </section>
        </div>
      </main>
    </>
  )
}
